import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Home
          </Button>
        </Link>

        <h1 className="text-4xl font-bold mb-4">Terms of Use</h1>
        <p className="text-muted-foreground mb-8">Effective date: August 21, 2026</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Agreement to these terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms of Use govern your access to the Brand Equity Analyzer web application at
              brandscore.biz (the "Service"), operated by TopServ Digital. By using the Service you agree to
              these terms. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Relationship to other agreements</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have a separate signed agreement, statement of work, or order form with TopServ Digital,
              that agreement controls for the services it covers and nothing in these terms limits, replaces, or
              overrides it. These terms apply only to your use of this Service where no signed agreement
              addresses that use.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Who may use the Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              You must be at least 18 years old and able to enter into a contract. If you use the Service on
              behalf of a business, you confirm you are authorized to act for that business, and "you" includes
              that business.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Accounts and guest use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may run an analysis as a guest or create an account. You are responsible for the accuracy of
              the information you submit, for keeping your credentials confidential, and for activity under your
              account. Tell us promptly if you believe your account has been accessed without authorization.
              Guest sessions are stored in your browser only and may be lost if you clear your browser storage.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Acceptable use</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Submit false, misleading, or unauthorized information, or impersonate any person or business</li>
              <li>Submit personal information about other individuals, or any sensitive data such as health,
                financial account, or government ID information</li>
              <li>Attempt to bypass authentication, rate limits, bot protection, or access controls</li>
              <li>Probe, scan, overload, scrape, or otherwise interfere with the Service or its infrastructure</li>
              <li>Reverse engineer, decompile, or copy the Service except as permitted by law</li>
              <li>Use the Service to violate any law, third-party rights, or third-party platform terms</li>
              <li>Resell or redistribute reports as your own product without our written permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Your content and license to us</h2>
            <p className="text-muted-foreground leading-relaxed">
              You keep ownership of the business information you submit. You grant us a limited license to use,
              process, and transmit that information to operate the Service, generate your reports, and provide
              support, including through the service providers listed in our{" "}
              <Link to="/privacy" className="underline text-brand-700">Privacy Policy</Link>. You confirm you
              have the rights needed to submit it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Reports, scores, and third-party data</h2>
            <p className="text-muted-foreground leading-relaxed">
              Brand equity scores and report content are automated estimates produced from third-party and
              publicly available data sources, some of which use AI processing. They may be incomplete,
              outdated, or inaccurate, and third-party sources may change or become unavailable. Reports are
              provided for general informational purposes and are not professional, legal, financial, or
              investment advice. You are responsible for decisions you make based on them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service, including its software, design, text, and branding, is owned by TopServ Digital or
              its licensors and is protected by intellectual property laws. We grant you a limited,
              non-exclusive, non-transferable, revocable right to use the Service and your own reports for your
              internal business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Third-party services and links</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service relies on and may link to third-party services. We do not control them and are not
              responsible for their content, availability, or practices. Your use of a third-party service is
              governed by that provider's own terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Availability and changes</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may change, suspend, or discontinue any part of the Service, and we may impose usage limits.
              We do not promise uninterrupted or error-free availability unless a signed agreement says
              otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Disclaimers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Except where a signed agreement provides otherwise, and to the maximum extent permitted by law,
              the Service is provided "as is" and "as available" without warranties of any kind, whether
              express, implied, or statutory, including implied warranties of merchantability, fitness for a
              particular purpose, non-infringement, and accuracy of results. Some jurisdictions do not allow
              certain disclaimers, so parts of this section may not apply to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Limitation of liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, TopServ Digital will not be liable for indirect,
              incidental, special, consequential, exemplary, or punitive damages, or for lost profits, revenue,
              data, or goodwill, arising from your use of the Service. Where the law permits a limit on our total
              liability, that limit is set by any signed agreement between us or, if there is none, by applicable
              law. Nothing here limits liability that cannot be limited by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Indemnity</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless TopServ Digital from claims, damages, and reasonable
              expenses arising from information you submit or from your use of the Service in breach of these
              terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">14. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may stop using the Service at any time and may ask us to delete your account. We may suspend
              or terminate access if you breach these terms or if we reasonably believe your use creates risk
              for the Service or other users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">15. Governing law and disputes</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have a signed agreement with TopServ Digital, the governing law and dispute resolution
              terms in that agreement apply. Otherwise, these terms are governed by applicable law, and no
              specific court, county, or venue is designated here; a dispute may be brought in any court of
              competent jurisdiction. Either party may seek injunctive relief where necessary to protect its
              rights, and nothing in this section removes any right you have to bring a claim where applicable
              law gives you that right.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">16. Changes to these terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms and will update the effective date above. Continued use of the Service
              after changes take effect means you accept the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">17. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about these terms:{" "}
              <a href="mailto:privacy@topservdigital.com" className="underline text-brand-700">
                privacy@topservdigital.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
